import LoadingSpinner from 'features/shared/components/loaders/LoadingSpinner';
import NoContentWrapper from 'features/shared/components/noContentWrapper/NoContentWrapper';
import Pagination from 'features/shared/components/pagination/Pagination';
import SimplePage from 'features/shared/layout/content/SimplePage';
import React, { ReactNode, useEffect, useState } from 'react';
import CustomerSelectList from 'features/customer/components/CustomerSelectList';
import CustomerSimplifiedDTO from 'features/customer/dtos/CustomerSimplifiedDTO';
import { useStateDataGrid } from 'features/shared/components/dataGrid/hooks/useStateDataGrid';
import DataQuery from 'data/DataQuery';
import { useQuery } from 'data/hooks/useQuery';
import PagedDataResultDTO from 'features/shared/dtos/PagedDataResultDTO';
import { loadDataFilterConfig } from 'features/shared/helpers/modules';
import DataGridHeader from 'features/shared/components/dataGrid/DataGridHeader';
import DataFilterModal from 'features/shared/components/dataFilterModal/DataFilterModal';
import { Customer_GetSimplified } from 'data/endpoints';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

const customersDataFilterJson = loadDataFilterConfig("customersDataFilter");
const pageSize = 12;

interface Props {
  automaticSelectCustomer: boolean;
  onCustomerClick: (customer: CustomerSimplifiedDTO) => Promise<void>;
  allCustomers?: boolean;
  renderDataGridWrapper?: (dataGridRender: () => JSX.Element) => JSX.Element;
}

const CustomerSelectSection = ({
  automaticSelectCustomer,
  onCustomerClick,
  allCustomers = false,
  renderDataGridWrapper,
}: Props) => {
  // translations
  const tLabels = useTranslation("customer\\customer").t;

  // state
  const [showLayoutAndSection, setLayoutAndSectionShow] = useState(false);

  // data grid hook
  const {
    dataGrid,
    descriptors,
    setDescriptors,
    showFilter,
    setShowFilter,
    paginationPageChangeHandler,
    filterApplyHandler,
    filterResetHandler,
    isApplyFilter,
    conditionalRefresh
  } = useStateDataGrid(pageSize, customersDataFilterJson);

  // fetch data
  const getQueryParams = (): URLSearchParams => {
    let queryParams = new URLSearchParams(dataGrid.computedUrlSearchParams);
    
    if (allCustomers) {
      queryParams.append("ForAdminUse", "true");      
    }
    
    return queryParams;
  };

  const query: DataQuery = {
    url: Customer_GetSimplified,
    params: getQueryParams()
  }
  const {data: customers, loadStatus} = useQuery<PagedDataResultDTO<CustomerSimplifiedDTO>>(query, { count: 0, result: [] }, [dataGrid.computedUrlSearchParams], { useTracker: true }, conditionalRefresh);

  // side effects
  useEffect(() => {
    if (automaticSelectCustomer) {
      if (loadStatus === "AfterFirstLoad") {
        if (customers.count === 1) {
          onCustomerClick(customers.result[0]);
        } else {
          setLayoutAndSectionShow(true);
        }
      }  
    }
  }, [loadStatus])

  // handlers
  const customerSelectHandler = async (customer: CustomerSimplifiedDTO) => { 
    await trackPromise(onCustomerClick(customer));
  }

  // sub-renders
  const renderCustomersGrid = () => {
    return (
      <NoContentWrapper
        data={customers}
        loadStatus={loadStatus}
        noContentInfo={tLabels("customerSelectSection.noCustomerFound")}
        noContentAsAlert
        isActiveFilter={isApplyFilter}
      >
        <DataGridHeader 
          count={customers.count}
          setShowFilterModal={setShowFilter}
          filterActive={isApplyFilter()}
        />
        <CustomerSelectList
          customers={customers.result}
          selectCustomerHandler={customerSelectHandler}
        />
        <div className="d-flex justify-content-center mt-4">
          <Pagination
            urlParams={new URLSearchParams(dataGrid.computedUrlSearchParams)}
            count={customers.count!}
            pageSize={pageSize}
            onPageChange={paginationPageChangeHandler}
          />
        </div>
        <DataFilterModal
          descriptors={descriptors}
          setDescriptors={setDescriptors}
          applyButtonOnClick={filterApplyHandler}
          resetButtonOnClick={filterResetHandler}
          show={showFilter}
          setShow={setShowFilter}
        />
      </NoContentWrapper>
    );
  }

  const renderCustomerGridInWrapper = () => {
    if (renderDataGridWrapper) {
      return renderDataGridWrapper(renderCustomersGrid);
    } else {
      return renderCustomersGrid();
    }
  }

  // render 
  if (automaticSelectCustomer) {
    if (showLayoutAndSection) {
      return renderCustomerGridInWrapper();
    } else {
      return (
        <>
          <LoadingSpinner useContainerBg={false} />
        </>
      );
    }
  } else {
    return renderCustomerGridInWrapper();
  }
}

export default CustomerSelectSection;
